import { useEffect, useMemo } from 'react';
import { useRecurly } from '@recurly/react-recurly';
import { useFeatureExperiment, FEATURE_FLAGS } from '../../FeaturesAndExperiments';

// Types from the Recurly Documentation : https://recurly.com/developers/reference/recurly-js/#paypal
type Token = {
  type: string;
  id: string;
  email: string;
  payer_id: string;
};

// https://recurly.com/developers/reference/recurly-js/#errors
type RecurlyPayPalErrorCodes = 'paypal-not-configured' | 'paypal-canceled' | 'paypal-error' | 'invalid-routing-number';

type RecurlyError = {
  name: string;
  code: RecurlyPayPalErrorCodes;
  message: string;
  fields?: string[];
};

type PayPalParams = {
  // called upon the "token" event of recurly-paypal, raised when a token is successfully generated
  onToken: ({ token }: { token: Token }) => void;
  // called upon the "error" event of recurly-paypal, raised when an error occurs
  onError: ({ error }: { error: RecurlyError }) => void;
  displayString?: string;
};

export const usePayPal = ({ onError, onToken, displayString }: PayPalParams) => {
  const recurly = useRecurly();
  const showPayPal = useFeatureExperiment(FEATURE_FLAGS.PAYPAL_INTEGRATION);

  const paypal = useMemo(() => {
    return recurly.PayPal({ payPalComplete: true, display: { displayName: displayString } });
  }, [displayString, recurly]);

  const onClick = () => {
    paypal.start();
  };

  useEffect(() => {
    if (paypal) {
      paypal.on('error', (error: RecurlyError) => {
        onError({ error });
      });

      paypal.on('token', (token: Token) => {
        onToken({ token });
      });
    }
  }, [onError, onToken, paypal]);

  return {
    onClick,
    showPayPal,
  };
};
